import { useState, useMemo } from 'react';
import {
  AreaChart,
  Area,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import ChartSelector, { type ChartType } from './ChartSelector';
import NLQPieChart from './PieChart';
import DataTable from './DataTable';
import MetricCard from './MetricCard';
import ComparisonGrid from './ComparisonGrid';
import { Sparkles } from 'lucide-react';

export type VisualizationType = 'auto' | ChartType;

interface SmartChartProps {
  data: any;
  parsed?: {
    intent?: string;
    dataSource?: string;
    groupBy?: string;
    visualization?: string;
  };
  insights?: string[];
  expanded?: boolean;
  onVisualizationChange?: (type: ChartType) => void;
}

interface ChartData {
  type: 'timeseries' | 'categorical' | 'metric' | 'list' | 'comparison' | 'comparison_bar';
  items: any[];
  valueKey: string;
  labelKey: string;
  secondaryValueKey?: string;
}

function analyzeData(data: any, _parsed?: SmartChartProps['parsed']): ChartData {
  // Comparison bar data (grouped bars format)
  if (Array.isArray(data) && data.length > 0 && 'current' in data[0] && 'previous' in data[0]) {
    return {
      type: 'comparison_bar',
      items: data,
      valueKey: 'current',
      labelKey: 'name',
      secondaryValueKey: 'previous',
    };
  }

  // Comparison data (current vs previous periods)
  if (typeof data === 'object' && data !== null && !Array.isArray(data) && 'current' in data && 'previous' in data) {
    const comparison = data as {
      current: { period: string; data: any };
      previous: { period: string; data: any };
      changes: Record<string, { value: number; change: number; direction: string }>;
    };

    // Transform comparison data into chart-friendly format
    const chartItems: any[] = [];
    const currentData = comparison.current.data || {};
    const previousData = comparison.previous.data || {};

    // Create items for each metric
    if ('totalRevenue' in currentData) {
      chartItems.push({
        metric: 'Revenue',
        current: currentData.totalRevenue || 0,
        previous: previousData.totalRevenue || 0,
        change: comparison.changes?.totalRevenue?.change || 0,
      });
    }
    if ('totalOrders' in currentData) {
      chartItems.push({
        metric: 'Orders',
        current: currentData.totalOrders || 0,
        previous: previousData.totalOrders || 0,
        change: comparison.changes?.totalOrders?.change || 0,
      });
    }
    if ('avgOrderValue' in currentData) {
      chartItems.push({
        metric: 'Avg Order',
        current: currentData.avgOrderValue || 0,
        previous: previousData.avgOrderValue || 0,
        change: comparison.changes?.avgOrderValue?.change || 0,
      });
    }

    return { type: 'comparison', items: chartItems, valueKey: 'current', labelKey: 'metric', secondaryValueKey: 'previous' };
  }

  // Single metric value
  if (typeof data === 'object' && data !== null && !Array.isArray(data)) {
    if ('totalRevenue' in data || 'totalOrders' in data || 'count' in data) {
      return { type: 'metric', items: [data], valueKey: 'value', labelKey: 'label' };
    }
    if ('lowStockItems' in data) {
      return { type: 'list', items: data.lowStockItems, valueKey: 'quantity', labelKey: 'name' };
    }
    if ('byCategory' in data) {
      return { type: 'categorical', items: data.byCategory, valueKey: 'totalCost', labelKey: '_id' };
    }
    if ('byStatus' in data) {
      return { type: 'categorical', items: data.byStatus, valueKey: 'count', labelKey: '_id' };
    }
  }

  // Array of items
  if (Array.isArray(data) && data.length > 0) {
    const firstItem = data[0];

    // Time series data (has _id or date that looks like a date string)
    const dateKey = firstItem._id && /^\d{4}-/.test(String(firstItem._id)) ? '_id' :
                    firstItem.date && /^\d{4}-/.test(String(firstItem.date)) ? 'date' : null;

    if (dateKey) {
      const valueKey = firstItem.revenue !== undefined ? 'revenue' :
                       firstItem.cost !== undefined ? 'cost' :
                       firstItem.totalCost !== undefined ? 'totalCost' :
                       firstItem.orders !== undefined ? 'orders' :
                       firstItem.members !== undefined ? 'members' :
                       firstItem.newMembers !== undefined ? 'newMembers' :
                       firstItem.count !== undefined ? 'count' : 'value';
      return {
        type: 'timeseries',
        items: data,
        valueKey,
        labelKey: dateKey,
        secondaryValueKey: firstItem.orders !== undefined && valueKey !== 'orders' ? 'orders' : undefined,
      };
    }

    // Categorical data (top items, by category)
    if (firstItem.name || firstItem._id) {
      const valueKey = firstItem.revenue !== undefined ? 'revenue' :
                       firstItem.quantity !== undefined ? 'quantity' :
                       firstItem.usageCount !== undefined ? 'usageCount' :
                       firstItem.count !== undefined ? 'count' : 'value';
      const labelKey = firstItem.name !== undefined ? 'name' : '_id';
      return { type: 'categorical', items: data, valueKey, labelKey };
    }

    return { type: 'list', items: data, valueKey: 'value', labelKey: 'name' };
  }

  return { type: 'metric', items: [], valueKey: 'value', labelKey: 'label' };
}

function selectBestVisualization(
  dataType: ChartData['type'],
  parsed?: SmartChartProps['parsed'],
  itemCount?: number
): ChartType {
  // If visualization explicitly requested
  if (parsed?.visualization && parsed.visualization !== 'auto') {
    return parsed.visualization as ChartType;
  }

  // Comparison data always uses bar chart
  if (dataType === 'comparison') return 'bar';

  // Based on intent
  if (parsed?.intent === 'compare') return 'bar';
  if (parsed?.intent === 'trend') return 'line';
  if (parsed?.intent === 'count') return 'kpi';
  if (parsed?.intent === 'top' || parsed?.intent === 'bottom') {
    return itemCount && itemCount > 10 ? 'table' : 'bar';
  }

  // Based on data type
  switch (dataType) {
    case 'timeseries':
      return 'line';
    case 'categorical':
      return itemCount && itemCount <= 6 ? 'pie' : 'bar';
    case 'metric':
      return 'kpi';
    case 'list':
      return 'table';
    default:
      return 'bar';
  }
}

function formatXAxis(value: string) {
  if (!value) return '';
  // Check if it's a date
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const date = new Date(value);
    return date.toLocaleDateString('en', { month: 'short', day: 'numeric' });
  }
  return value;
}

export default function SmartChart({
  data,
  parsed,
  insights,
  expanded = false,
  onVisualizationChange,
}: SmartChartProps) {
  const analyzed = useMemo(() => analyzeData(data, parsed), [data, parsed]);
  const defaultType = useMemo(
    () => selectBestVisualization(analyzed.type, parsed, analyzed.items.length),
    [analyzed, parsed]
  );

  const [chartType, setChartType] = useState<ChartType>(defaultType);

  const handleTypeChange = (type: ChartType) => {
    setChartType(type);
    onVisualizationChange?.(type);
  };

  const chartHeight = expanded ? 320 : 200;

  // Determine available chart types based on data
  const availableTypes = useMemo((): ChartType[] => {
    switch (analyzed.type) {
      case 'timeseries':
        return ['line', 'area', 'bar', 'table'];
      case 'categorical':
        return ['bar', 'pie', 'table'];
      case 'metric':
        return ['kpi', 'table'];
      case 'list':
        return ['table', 'bar'];
      default:
        return ['bar', 'line', 'pie', 'table', 'kpi'];
    }
  }, [analyzed.type]);

  // Render insights
  const renderInsights = () => {
    if (!insights?.length) return null;
    return (
      <div className="mb-3 space-y-1">
        {insights.filter(Boolean).map((insight, i) => (
          <p key={i} className="text-sm text-muted-foreground flex items-start gap-2">
            <Sparkles className="h-3 w-3 mt-1 text-yellow-500 shrink-0" />
            {insight}
          </p>
        ))}
      </div>
    );
  };

  // Render KPI/Metric
  if (chartType === 'kpi') {
    const firstItem = analyzed.items[0];
    if (!firstItem) return <div className="text-muted-foreground">No data</div>;

    // Handle different metric structures
    let value: number | string = 0;
    let label = 'Total';

    if ('totalRevenue' in firstItem) {
      value = firstItem.totalRevenue;
      label = 'Total Revenue';
    } else if ('totalOrders' in firstItem) {
      value = firstItem.totalOrders;
      label = 'Total Orders';
    } else if ('count' in firstItem) {
      value = firstItem.count;
      label = 'Count';
    } else if ('totalMembers' in firstItem) {
      value = firstItem.totalMembers;
      label = 'Total Members';
    }

    const metricInsights: string[] = [];
    if ('totalOrders' in firstItem && 'avgOrderValue' in firstItem) {
      metricInsights.push(`Total orders: ${firstItem.totalOrders?.toLocaleString()}`);
      metricInsights.push(`Average order value: ${firstItem.avgOrderValue?.toLocaleString()}`);
    }

    return (
      <div>
        {renderInsights()}
        <MetricCard
          value={value}
          label={label}
          prefix={label.includes('Revenue') ? '฿' : undefined}
          insights={metricInsights}
          size={expanded ? 'lg' : 'md'}
        />
        <div className="mt-2 flex justify-end">
          <ChartSelector
            selected={chartType}
            onChange={handleTypeChange}
            availableTypes={availableTypes}
          />
        </div>
      </div>
    );
  }

  // Render Table
  if (chartType === 'table') {
    return (
      <div>
        {renderInsights()}
        <DataTable
          data={analyzed.items}
          maxRows={expanded ? 50 : 10}
          compact={!expanded}
        />
        <div className="mt-2 flex justify-end">
          <ChartSelector
            selected={chartType}
            onChange={handleTypeChange}
            availableTypes={availableTypes}
          />
        </div>
      </div>
    );
  }

  // Render Pie Chart
  if (chartType === 'pie' || chartType === 'donut') {
    const pieData = analyzed.items.map((item) => ({
      name: typeof item[analyzed.labelKey] === 'object'
        ? item[analyzed.labelKey]?.en || String(item[analyzed.labelKey])
        : String(item[analyzed.labelKey] || 'Unknown'),
      value: item[analyzed.valueKey] || 0,
    }));

    return (
      <div>
        {renderInsights()}
        <NLQPieChart
          data={pieData}
          donut={chartType === 'donut'}
          height={chartHeight}
          showLegend={expanded}
        />
        <div className="mt-2 flex justify-end">
          <ChartSelector
            selected={chartType}
            onChange={handleTypeChange}
            availableTypes={availableTypes}
          />
        </div>
      </div>
    );
  }

  // Check if this is comparison bar data (grouped bars for comparison)
  if (analyzed.type === 'comparison_bar') {
    const comparisonBarData = analyzed.items.map((item) => ({
      name: item.name,
      'This Week': item.current,
      'Last Week': item.previous,
      change: item.change,
      direction: item.direction,
    }));

    return (
      <div>
        {renderInsights()}
        <div style={{ height: chartHeight }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={comparisonBarData} layout="vertical" margin={{ left: 80 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => v.toLocaleString()} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={75} />
              <Tooltip
                formatter={(value, name) => [
                  name?.toString().includes('Week') ? `฿${Number(value || 0).toLocaleString()}` : Number(value || 0).toLocaleString(),
                  name?.toString() || '',
                ]}
                contentStyle={{
                  backgroundColor: 'hsl(var(--background))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px',
                }}
              />
              <Bar dataKey="This Week" fill="#22c55e" name="This Week" radius={[0, 4, 4, 0]} />
              <Bar dataKey="Last Week" fill="#f59e0b" name="Last Week" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-2 flex items-center justify-between">
          <div className="flex gap-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded bg-green-500"></span> This Week
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded bg-amber-500"></span> Last Week
            </span>
          </div>
          <ChartSelector
            selected={chartType}
            onChange={handleTypeChange}
            availableTypes={availableTypes}
          />
        </div>
      </div>
    );
  }

  // Check if this is comparison data (has current/previous structure)
  const isComparison = analyzed.type === 'comparison' ||
    (typeof data === 'object' && data !== null && 'current' in data && 'previous' in data && 'changes' in data);

  // Render Comparison using KPI Grid (best practice for period-over-period comparison)
  // Research shows: metrics with different scales should NOT be on the same chart
  // Instead, use individual metric cards with their own context
  if (isComparison) {
    // Pass the raw comparison data to ComparisonGrid
    const comparisonData = data as {
      current: { period: string; data: Record<string, number> };
      previous: { period: string; data: Record<string, number> };
      changes: Record<string, { value: number; change: number; direction: 'up' | 'down' | 'stable' }>;
    };

    return (
      <div>
        {renderInsights()}
        <ComparisonGrid data={comparisonData} expanded={expanded} />
      </div>
    );
  }

  // Render Line/Area/Bar Chart (regular data)
  const chartData = analyzed.items.map((item) => ({
    ...item,
    label: typeof item[analyzed.labelKey] === 'object'
      ? item[analyzed.labelKey]?.en
      : item[analyzed.labelKey],
  }));

  const ChartComponent = chartType === 'area' ? AreaChart : chartType === 'line' ? LineChart : BarChart;

  return (
    <div>
      {renderInsights()}
      <div style={{ height: chartHeight }}>
        <ResponsiveContainer width="100%" height="100%">
          <ChartComponent data={chartData}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis
              dataKey={analyzed.labelKey === '_id' || analyzed.labelKey === 'date' ? analyzed.labelKey : 'label'}
              tick={{ fontSize: 10 }}
              className="text-muted-foreground"
              tickFormatter={formatXAxis}
            />
            <YAxis tick={{ fontSize: 10 }} className="text-muted-foreground" />
            <Tooltip
              formatter={(value) => [
                analyzed.valueKey.includes('revenue') || analyzed.valueKey.includes('cost')
                  ? `฿${Number(value).toLocaleString()}`
                  : Number(value).toLocaleString(),
                analyzed.valueKey,
              ]}
              labelFormatter={formatXAxis}
              contentStyle={{
                backgroundColor: 'hsl(var(--background))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px',
              }}
            />
            {chartType === 'area' ? (
              <Area
                type="monotone"
                dataKey={analyzed.valueKey}
                stroke="hsl(var(--primary))"
                fill="hsl(var(--primary) / 0.2)"
              />
            ) : chartType === 'line' ? (
              <Line
                type="monotone"
                dataKey={analyzed.valueKey}
                stroke="hsl(var(--primary))"
                strokeWidth={2}
                dot={{ fill: 'hsl(var(--primary))', strokeWidth: 0, r: 3 }}
              />
            ) : (
              <Bar dataKey={analyzed.valueKey} fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
            )}
          </ChartComponent>
        </ResponsiveContainer>
      </div>
      <div className="mt-2 flex justify-end">
        <ChartSelector
          selected={chartType}
          onChange={handleTypeChange}
          availableTypes={availableTypes}
        />
      </div>
    </div>
  );
}
